/**
 * Zod source-of-truth for the FormDefinition shape.
 *
 * `form_versions.definition` (jsonb) is validated against `formDefinitionSchema`
 * on every read by the storefront renderer and the submit endpoint — the DB
 * stores arbitrary jsonb but the read sites refuse to operate on malformed
 * shapes.
 *
 * The form-creator AI agent's tool input_schema is derived from this same
 * schema (z.toJSONSchema) so the model literally cannot emit out-of-allowlist
 * field types, conditional ops, or routing actions. See plan §5 for the
 * strict-input-schema rationale.
 */

import { z } from 'zod';

// ─── Primitives ───────────────────────────────────────────────────────────

/**
 * Permissive UUID validator — inlined copy of packages/shared-utils/src/zUuid.ts.
 * @sifly/forms keeps zero workspace dependencies (only zod), so the helper
 * is duplicated here rather than introducing a dep on shared-utils. Accepts
 * the canonical 8-4-4-4-12 hex shape regardless of RFC version byte,
 * matching what Postgres's `uuid` column type accepts. See shared-utils
 * for the full rationale (SiFly USA's deliberately hand-crafted platform id).
 */
const zUuid = () =>
  z
    .string()
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Invalid UUID format (expected 8-4-4-4-12 hex)',
    );

/** Field key — used as the JSON object key on submitted `data`. */
const fieldKey = z
  .string()
  .min(1)
  .max(60)
  .regex(/^[a-z][a-z0-9_]*$/, 'Must be snake_case starting with a letter');

const fieldLabel = z.string().min(1).max(120);
const helpText = z.string().max(240).optional();
const placeholder = z.string().max(120).optional();

/** Option for select / multiselect / radio. */
export const fieldOptionSchema = z.object({
  value: z.string().min(1).max(120),
  label: z.string().min(1).max(120),
});

// ─── Field discriminated union ────────────────────────────────────────────
// One variant per supported field type. All variants share the base fields
// (key, label, helpText, required, placeholder). Type-specific config lives
// alongside.

const baseFieldShape = {
  key: fieldKey,
  label: fieldLabel,
  helpText,
  required: z.boolean().default(false),
  placeholder,
};

export const textFieldSchema = z.object({
  type: z.literal('text'),
  ...baseFieldShape,
  minLength: z.number().int().min(0).max(10000).optional(),
  maxLength: z.number().int().min(1).max(10000).optional(),
  pattern: z.string().max(200).optional(),
  mask: z.string().max(120).optional(),
  defaultValue: z.string().optional(),
});

export const emailFieldSchema = z.object({
  type: z.literal('email'),
  ...baseFieldShape,
  defaultValue: z.string().optional(),
});

export const phoneFieldSchema = z.object({
  type: z.literal('phone'),
  ...baseFieldShape,
  defaultCountry: z.string().length(2).optional(),
  defaultValue: z.string().optional(),
});

/**
 * Country — a searchable country dropdown (ISO 3166-1 alpha-2 code as the
 * stored value). The renderer supplies the country list and pins the dealer's
 * favourite countries (`tenants.favorite_countries`); no options are authored
 * here. When no `defaultValue` is set, the renderer seeds the field with the
 * dealer's default country, so a waiver/contact form lands pre-filled.
 */
export const countryFieldSchema = z.object({
  type: z.literal('country'),
  ...baseFieldShape,
  defaultValue: z.string().length(2).optional(),
});

/**
 * Region — the state / province / region field. Country-aware: when the form's
 * `country` field is the US the renderer shows a state dropdown; otherwise a
 * free-text input labelled generically. No config — it auto-binds to the
 * form's country field. Mirrors the portal's order/contact address behaviour.
 */
export const regionFieldSchema = z.object({
  type: z.literal('region'),
  ...baseFieldShape,
  defaultValue: z.string().optional(),
});

export const numberFieldSchema = z.object({
  type: z.literal('number'),
  ...baseFieldShape,
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().positive().optional(),
  defaultValue: z.number().optional(),
});

export const textareaFieldSchema = z.object({
  type: z.literal('textarea'),
  ...baseFieldShape,
  minLength: z.number().int().min(0).max(10000).optional(),
  maxLength: z.number().int().min(1).max(10000).optional(),
  rows: z.number().int().min(2).max(20).default(4),
  defaultValue: z.string().optional(),
});

export const selectFieldSchema = z.object({
  type: z.literal('select'),
  ...baseFieldShape,
  options: z.array(fieldOptionSchema).min(1).max(100),
  defaultValue: z.string().optional(),
});

export const multiselectFieldSchema = z.object({
  type: z.literal('multiselect'),
  ...baseFieldShape,
  options: z.array(fieldOptionSchema).min(1).max(100),
  minSelections: z.number().int().min(0).max(100).optional(),
  maxSelections: z.number().int().min(1).max(100).optional(),
  defaultValue: z.array(z.string()).optional(),
});

export const radioFieldSchema = z.object({
  type: z.literal('radio'),
  ...baseFieldShape,
  options: z.array(fieldOptionSchema).min(2).max(20),
  defaultValue: z.string().optional(),
});

export const checkboxFieldSchema = z.object({
  type: z.literal('checkbox'),
  ...baseFieldShape,
  defaultValue: z.boolean().optional(),
});

export const dateFieldSchema = z.object({
  type: z.literal('date'),
  ...baseFieldShape,
  min: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  max: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  defaultValue: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});

export const consentFieldSchema = z.object({
  type: z.literal('consent'),
  ...baseFieldShape,
  consentText: z.string().min(1).max(600),
});

/**
 * Signature — a drawn signature captured on a canvas. The submitted value is a
 * PNG `data:` URL (the only field whose value is a base64 image rather than a
 * plain scalar). No type-specific config: a signature is a signature. The
 * renderer draws the pad; the validator (see validator.ts) enforces the PNG
 * data-URL shape + a size cap so the jsonb row stays lean.
 */
export const signatureFieldSchema = z.object({
  type: z.literal('signature'),
  ...baseFieldShape,
});

export const hiddenFieldSchema = z.object({
  type: z.literal('hidden'),
  key: fieldKey,
  label: fieldLabel,
  defaultValue: z.string().max(400).optional(),
});

export const fieldSchema = z.discriminatedUnion('type', [
  textFieldSchema,
  emailFieldSchema,
  phoneFieldSchema,
  countryFieldSchema,
  regionFieldSchema,
  numberFieldSchema,
  textareaFieldSchema,
  selectFieldSchema,
  multiselectFieldSchema,
  radioFieldSchema,
  checkboxFieldSchema,
  dateFieldSchema,
  consentFieldSchema,
  signatureFieldSchema,
  hiddenFieldSchema,
]);

/** Allowed field types — kept in sync with the discriminated union above. */
export const FIELD_TYPES = [
  'text',
  'email',
  'phone',
  'country',
  'region',
  'number',
  'textarea',
  'select',
  'multiselect',
  'radio',
  'checkbox',
  'date',
  'consent',
  'signature',
  'hidden',
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

// ─── Steps ────────────────────────────────────────────────────────────────

const stepKey = z
  .string()
  .min(1)
  .max(60)
  .regex(/^[a-z][a-z0-9_]*$/, 'Step keys must be snake_case');

export const stepSchema = z.object({
  key: stepKey,
  title: z.string().min(1).max(120),
  description: z.string().max(300).optional(),
  /** Field keys (from the form's `fields` array) belonging to this step. */
  fieldKeys: z.array(fieldKey).min(1),
});

// ─── Conditional rules ────────────────────────────────────────────────────

export const CONDITIONAL_OPS = [
  'equals',
  'not_equals',
  'in',
  'not_in',
  'is_empty',
  'is_not_empty',
] as const;
export type ConditionalOp = (typeof CONDITIONAL_OPS)[number];

export const CONDITIONAL_ACTIONS = ['show', 'hide', 'require', 'optional'] as const;
export type ConditionalAction = (typeof CONDITIONAL_ACTIONS)[number];

/**
 * A single rule evaluated against current form values. The same evaluator
 * runs client-side (for show/hide) and server-side (final validation —
 * a "required-if X" rule), which is why this lives in @sifly/forms.
 */
export const conditionalRuleSchema = z.object({
  /** The field whose visibility / required-ness is being controlled. */
  targetFieldKey: fieldKey,
  /** The field whose value is being tested. */
  sourceFieldKey: fieldKey,
  op: z.enum(CONDITIONAL_OPS),
  /** Comparison value — required for equals/not_equals/in/not_in. */
  value: z
    .union([z.string(), z.number(), z.boolean(), z.array(z.string())])
    .optional(),
  action: z.enum(CONDITIONAL_ACTIONS),
});

// ─── Routing rules ────────────────────────────────────────────────────────

export const ROUTING_ACTIONS = [
  'apply_tag',
  'assign_dealer',
  'set_pipeline',
  'notify_email',
] as const;
export type RoutingAction = (typeof ROUTING_ACTIONS)[number];

/**
 * A routing rule fires when ALL `when` clauses match. Empty `when` means
 * the rule always fires (useful for a default-tag rule).
 *
 * NOTE: `then.assignDealerId` and `then.ghlPipeline.id` carry real DB ids.
 * The form-creator AI agent's tool schema strips these to prevent
 * hallucinated identifiers — the dealer fills them in via dropdown in the
 * routing panel before publish. See plan §Risk callouts #7.
 */
export const routingRuleSchema = z.object({
  id: z.string().min(1).max(60),
  description: z.string().max(200).optional(),
  when: z.array(conditionalRuleSchema.omit({ action: true, targetFieldKey: true })),
  then: z.object({
    tags: z.array(z.string().min(1).max(60)).optional(),
    assignDealerId: zUuid().optional(),
    ghlPipeline: z
      .object({
        id: z.string().min(1).max(120),
        stageId: z.string().min(1).max(120).optional(),
      })
      .optional(),
    notifyEmails: z.array(z.string().email()).max(10).optional(),
  }),
});

// ─── Settings ─────────────────────────────────────────────────────────────

/**
 * Where a post-submit redirect may send a visitor — the ONLY shapes it may
 * take. Checked wherever a definition is PARSED (the builder, the live
 * renderer, the submit route), not only where it is written: a value that
 * reaches `form_versions` directly and fails this takes the form off the
 * site rather than running. Checked again at the one place a browser follows
 * it (`FormRender`, which assigns it to `window.location.href`).
 *
 * Why an allowlist and not a `javascript:` blocklist: the value reaches
 * `location.href` verbatim, so ANY scheme a browser will act on is script or
 * navigation the dealer never chose — `javascript:`, `data:`, `vbscript:`,
 * and the ones nobody has thought of yet. And a definition does not only
 * arrive through the form builder: a tenant member can write `form_versions`
 * straight through the Data API (RLS admits it), and the AI form agent emits
 * one. Before this, nothing checked the value at all.
 *
 * Allowed:
 *   • a path on the dealer's own site — `/…`, but never `//…` or `/\…`,
 *     which a browser reads as a protocol-relative OFF-site address;
 *   • an `https://` address (a dealer may send people to a booking page on
 *     another site).
 *
 * Rejected anywhere in the value: whitespace, ASCII control characters and
 * backslashes. Browsers strip tabs and newlines from a URL before parsing it,
 * so `/<TAB>/evil.example` becomes `//evil.example`, and a backslash after the
 * leading slash is read as a second slash. `http://` is refused.
 *
 * Ported from WOWsite (wowsite#215); SiFly has no picked-page sentinels, so
 * the two site-path / https shapes are the whole rule.
 */
// Only the `https` scheme may vary in case — deliberately not an `i` regex.
// After `https://` the first character must start a host: not a slash or
// backslash, and not `?` / `#` either (`https://?` passes as text but is a
// parse failure a browser refuses to navigate to).
const SAFE_REDIRECT_PATTERN = /^(?:\/(?![/\\])|[Hh][Tt][Tt][Pp][Ss]:\/\/[^/\\?#])/;
// eslint-disable-next-line no-control-regex
const UNSAFE_REDIRECT_CHAR = /[\s\\\u0000-\u001f\u007f]/;

export function isSafeRedirectTarget(url: string): boolean {
  return SAFE_REDIRECT_PATTERN.test(url) && !UNSAFE_REDIRECT_CHAR.test(url);
}

export const successBehaviorSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('message'),
    title: z.string().min(1).max(120).default('Thanks!'),
    body: z.string().max(600).optional(),
  }),
  z.object({
    kind: z.literal('redirect'),
    url: z
      .string()
      .min(1)
      .max(600)
      .refine(isSafeRedirectTarget, {
        message: 'Redirect must be a path on your site (like /contact) or an https:// address',
      }),
  }),
]);

export const spamProtectionSchema = z.object({
  honeypot: z.boolean().default(true),
  timeTrap: z.boolean().default(true),
  /** Minimum seconds between render and submit. Defeats fast-fire bots. */
  timeTrapMinSeconds: z.number().int().min(0).max(60).default(2),
  turnstile: z.boolean().default(false),
});

export const triageConfigSchema = z.object({
  /**
   * Per-form override of the tenant default. NULL/undefined → inherit
   * tenant `triage_default_enabled`.
   */
  enabled: z.boolean().nullable().optional(),
  /** Free-text instructions appended to the tenant's default triage prompt. */
  instructions: z.string().max(2000).nullable().optional(),
});

export const formSettingsSchema = z.object({
  submitLabel: z.string().min(1).max(40).default('Submit'),
  successBehavior: successBehaviorSchema.default({ kind: 'message', title: 'Thanks!' }),
  spamProtection: spamProtectionSchema.default({
    honeypot: true,
    timeTrap: true,
    timeTrapMinSeconds: 2,
    turnstile: false,
  }),
  triage: triageConfigSchema.default({}),
});

// ─── Top-level FormDefinition ─────────────────────────────────────────────

export const FORM_DEFINITION_SCHEMA_VERSION = 1 as const;

export const formDefinitionSchema = z
  .object({
    schemaVersion: z.literal(FORM_DEFINITION_SCHEMA_VERSION),
    fields: z.array(fieldSchema).min(1).max(80),
    /**
     * Steps for wizard mode. If `steps.length <= 1`, the renderer shows a
     * single-page form. When steps are present, every non-hidden field key
     * must appear in exactly one step's `fieldKeys` (enforced by
     * `validateFormDefinition`).
     */
    steps: z.array(stepSchema).max(10).default([]),
    conditionalRules: z.array(conditionalRuleSchema).max(60).default([]),
    routingRules: z.array(routingRuleSchema).max(40).default([]),
    settings: formSettingsSchema.default({} as never),
  })
  .superRefine((def, ctx) => {
    // Unique field keys.
    const seenKeys = new Set<string>();
    for (const f of def.fields) {
      if (seenKeys.has(f.key)) {
        ctx.addIssue({
          code: 'custom',
          message: `Duplicate field key: ${f.key}`,
          path: ['fields'],
        });
      }
      seenKeys.add(f.key);
    }

    // Step coverage: every visible field must be in exactly one step
    // (only when steps are used).
    if (def.steps.length > 0) {
      const stepFieldKeys = new Map<string, number>();
      for (const step of def.steps) {
        for (const fk of step.fieldKeys) {
          stepFieldKeys.set(fk, (stepFieldKeys.get(fk) ?? 0) + 1);
        }
      }
      for (const f of def.fields) {
        if (f.type === 'hidden') continue;
        const count = stepFieldKeys.get(f.key) ?? 0;
        if (count === 0) {
          ctx.addIssue({
            code: 'custom',
            message: `Field "${f.key}" is not assigned to any step`,
            path: ['steps'],
          });
        } else if (count > 1) {
          ctx.addIssue({
            code: 'custom',
            message: `Field "${f.key}" is assigned to multiple steps`,
            path: ['steps'],
          });
        }
      }
      // Step keys themselves must be unique.
      const seenStepKeys = new Set<string>();
      for (const s of def.steps) {
        if (seenStepKeys.has(s.key)) {
          ctx.addIssue({
            code: 'custom',
            message: `Duplicate step key: ${s.key}`,
            path: ['steps'],
          });
        }
        seenStepKeys.add(s.key);
      }
    }

    // Conditional and routing rules must reference real field keys.
    const allKeys = new Set(def.fields.map((f) => f.key));
    for (let i = 0; i < def.conditionalRules.length; i++) {
      const r = def.conditionalRules[i]!;
      if (!allKeys.has(r.sourceFieldKey)) {
        ctx.addIssue({
          code: 'custom',
          message: `Conditional rule sourceFieldKey "${r.sourceFieldKey}" does not exist`,
          path: ['conditionalRules', i, 'sourceFieldKey'],
        });
      }
      if (!allKeys.has(r.targetFieldKey)) {
        ctx.addIssue({
          code: 'custom',
          message: `Conditional rule targetFieldKey "${r.targetFieldKey}" does not exist`,
          path: ['conditionalRules', i, 'targetFieldKey'],
        });
      }
    }
    for (let i = 0; i < def.routingRules.length; i++) {
      const r = def.routingRules[i]!;
      for (let j = 0; j < r.when.length; j++) {
        const w = r.when[j]!;
        if (!allKeys.has(w.sourceFieldKey)) {
          ctx.addIssue({
            code: 'custom',
            message: `Routing rule when[${j}].sourceFieldKey "${w.sourceFieldKey}" does not exist`,
            path: ['routingRules', i, 'when', j, 'sourceFieldKey'],
          });
        }
      }
    }
  });

/**
 * Convenience wrapper around `formDefinitionSchema.safeParse`. Used by:
 *   - the storefront submit endpoint (re-validates the stored definition
 *     before trusting it)
 *   - the form-creator AI agent's server-side faithfulness check (rejects
 *     malformed emissions before they reach the client)
 */
export function validateFormDefinition(input: unknown) {
  return formDefinitionSchema.safeParse(input);
}
